/**
 * PayPal checkout (web). The PayPal JS SDK renders the buttons; our server creates the order (price from the
 * catalogue) and verifies + fulfils it after approval, so nothing the browser reports is trusted.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { errorMessage } from '../lib/hooks';
import { useSession } from '../store/session';
import { Modal } from './ui';

interface PayPalButtons {
  render(el: HTMLElement): Promise<void>;
  close(): Promise<void>;
}
interface PayPalSdk {
  Buttons(o: {
    style?: Record<string, unknown>;
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }) => Promise<void>;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
  }): PayPalButtons;
}

let sdkPromise: Promise<PayPalSdk> | null = null;
function loadSdk(clientId: string): Promise<PayPalSdk> {
  const w = window as unknown as { paypal?: PayPalSdk };
  if (w.paypal) return Promise.resolve(w.paypal);
  sdkPromise ??= new Promise<PayPalSdk>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture&disable-funding=credit,paylater`;
    s.async = true;
    s.onload = () => (w.paypal ? resolve(w.paypal) : reject(new Error('PayPal failed to load')));
    s.onerror = () => {
      sdkPromise = null;
      reject(new Error('Could not reach PayPal. Check your connection and try again.'));
    };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export function usePayPalClientId(): string | null {
  return useSession((s) => (s.boot as { paypalClientId?: string | null } | null)?.paypalClientId ?? null);
}

function Buttons({ clientId, productId, onPaid, onClose }: { clientId: string; productId: string; onPaid: () => void; onClose: () => void }) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'processing'>('loading');
  const cb = useRef({ onPaid, onClose });
  cb.current = { onPaid, onClose };

  useEffect(() => {
    let buttons: PayPalButtons | null = null;
    let cancelled = false;
    loadSdk(clientId)
      .then((sdk) => {
        if (cancelled || !host.current) return;
        buttons = sdk.Buttons({
          style: { layout: 'vertical', shape: 'pill', label: 'pay' },
          createOrder: async () => (await api.post<{ orderId: string }>('/api/v1/store/paypal/orders', { productId })).orderId,
          onApprove: async (data) => {
            setState('processing');
            try {
              await api.post('/api/v1/store/purchases/verify', { platform: 'paypal', productId, receipt: data.orderID, idempotencyKey: `pp-${data.orderID}` });
              cb.current.onPaid();
            } catch (e) {
              setState('ready');
              setError(errorMessage(e));
            }
          },
          onCancel: () => setError(null),
          onError: (e) => setError(e instanceof Error ? e.message : 'PayPal reported an error. You have not been charged.'),
        });
        return buttons.render(host.current).then(() => !cancelled && setState('ready'));
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'PayPal is unavailable'));
    return () => {
      cancelled = true;
      void buttons?.close().catch(() => undefined);
    };
  }, [clientId, productId]);

  return (
    <div className="col" style={{ gap: 12, minWidth: 260 }}>
      {state === 'loading' && !error && <span className="dim">Loading PayPal…</span>}
      {state === 'processing' && <span className="dim">Confirming your payment…</span>}
      <div ref={host} style={{ display: state === 'processing' ? 'none' : undefined }} />
      {error && (
        <p role="alert" style={{ color: 'var(--danger)', margin: 0 }}>
          {error}
        </p>
      )}
      <button className="btn ghost block" onClick={onClose} disabled={state === 'processing'}>
        Cancel
      </button>
    </div>
  );
}

export function PayPalCheckout({ open, title, priceLabel, productId, onPaid, onClose }: { open: boolean; title: string; priceLabel: string; productId: string; onPaid: () => void; onClose: () => void }) {
  const clientId = usePayPalClientId();
  if (!clientId) return null;
  return (
    <Modal open={open} onClose={onClose} title={`${title} · ${priceLabel}`} dismissable={false}>
      <Buttons clientId={clientId} productId={productId} onPaid={onPaid} onClose={onClose} />
    </Modal>
  );
}
